/**
 * persistDocument
 *
 * Firebase operations for a single Firestore document.
 * Loads data via onSnapshot and provides write operations.
 *
 * Writes `state.error` on one path only: every delivered snapshot clears it and
 * the listener's error callback sets it. That tells an absent document apart
 * from a listener that never delivered — both leave `loading` false and `data`
 * null — and `onError` is not a substitute, because `createDocumentStore` keeps
 * only the first connector's. The callback is still invoked for single-connector
 * consumers, and `formStore` and `entityRecordStore` route it back onto their
 * own `error` slot, so on those two stores the field also carries write
 * failures, which the next delivered snapshot then clears.
 *
 * Second channel: a from-cache non-existence for a never-loaded document is a
 * listener that has not delivered — the SDK raises it merely because the client
 * is offline — so it is dropped, leaving `loading` true rather than reporting a
 * confirmed absence.
 *
 * Third channel: `flushPending` rejects. A debounced body cannot reject on its
 * own — lodash invokes it from a timer with nothing attached to the returned
 * promise — so each one parks its failure on `debouncedError`, which
 * `flushPending` rethrows. That is the only way a caller that armed a debounced
 * write learns it did not land. The parked error is read at the end of a flush,
 * never cleared on entry, and dropped only once every concurrent flush has read
 * it (`flushDepth`) or once a retry lands the payload that failed — so
 * overlapping flushers cannot wipe a failure from one another, and a flush can
 * never resolve successfully over a debounced write that failed and has not
 * since landed. A non-debounced failure never reaches this channel: it rejects
 * its own caller, and `flushPending` neither retries nor re-reports it.
 */

import {
  doc,
  deleteDoc,
  onSnapshot,
  updateDoc,
  setDoc,
  Timestamp,
  serverTimestamp,
  type UpdateData,
} from 'firebase/firestore';
import debounce from 'lodash/debounce';
import { firestore } from '@/services/startFirestore';
import { SAVE_DEBOUNCE_MS } from '#types/System.Parameters.types';
import type { DocumentState, DocumentPersist, PersistOperationOptions } from '../Store.types';
import { mergeDocumentIntoProxy } from '../core/mergeDocumentIntoProxy';
import { createWriteQueue, type WriteQueueState } from './createWriteQueue';

type PersistDocumentResult<T> = {
  persist: DocumentPersist<T>;
  unsubscribe: () => void;
};

/**
 * Passes `flushPending` makes at the update queue before giving up and
 * rejecting. A pass already waits out whatever holds the queue before firing, so
 * one lands the queued payload; the second waits out a sibling `set`/`setMerge`
 * that took the queue while the first was awaiting; the third absorbs one round
 * of newly-armed data from another writer. A failed pass does not consume the
 * budget — it returns immediately and `flushPending` reports the parked error —
 * so the cap only bounds a document being written to continuously from
 * elsewhere, which must not hold a flush open forever.
 */
const MAX_FLUSH_PASSES = 3;

/**
 * Creates Firebase persistence operations for a document.
 * Sets up onSnapshot to load data into the provided state.
 *
 * @param path - Firestore document path
 * @param state - Valtio state to populate
 * @param onError - Optional error callback
 */
export function persistDocument<T extends Record<string, unknown>>(
  path: string,
  state: DocumentState<T>,
  onError?: (error: Error) => void
): PersistDocumentResult<T> {
  const docRef = doc(firestore, path);
  const docId = docRef.id;

  type DeferredSnapshot =
    | { exists: false }
    | { exists: true; data: T };

  type DocWriteState = WriteQueueState<DeferredSnapshot> & {
    updatePending?: UpdateData<T>;
    updateInFlight?: UpdateData<T>;
    /** The debounced update body currently running, so a flush can wait for it before firing again. */
    updateSettled?: Promise<void>;
    /** First failure since the last `flushPending`, which rethrows it. */
    debouncedError?: Error;
    /** `flushPending` calls in progress; the last one out clears `debouncedError`. */
    flushDepth?: number;
    updateDebounced?: ReturnType<typeof debounce<() => Promise<void>>>;
    setDebounced?: ReturnType<typeof debounce<(d: T) => Promise<void>>>;
    setMergeDebounced?: ReturnType<typeof debounce<(d: Partial<T>) => Promise<void>>>;
  };

  const applyDeferredSnapshot = (deferred: DeferredSnapshot): void => {
    if (!deferred.exists) {
      state.data = null;
      return;
    }

    const localTime = ((state.data as Record<string, unknown> | null)?.modifiedAt as Timestamp | undefined)?.toMillis?.() ?? 0;
    const incomingTime = ((deferred.data as Record<string, unknown>).modifiedAt as Timestamp | undefined)?.toMillis?.() ?? 0;
    if (incomingTime > 0 && localTime > 0 && incomingTime < localTime) {
      return;
    }

    if (state.data === null) {
      state.data = { ...deferred.data, docId } as T;
    } else {
      mergeDocumentIntoProxy(
        state.data as Record<string, unknown>,
        deferred.data as Record<string, unknown>,
        docId
      );
    }
  };

  const writeQueue = createWriteQueue<DeferredSnapshot>({
    onApplyDeferred: applyDeferredSnapshot,
  });

  const writeState = writeQueue.state as DocWriteState;

  const cancelNonUpdateDebouncedOps = (): void => {
    writeState.setDebounced?.cancel();
    writeState.setDebounced = undefined;

    writeState.setMergeDebounced?.cancel();
    writeState.setMergeDebounced = undefined;
  };

  const unsubscribe = onSnapshot(
    docRef,
    { includeMetadataChanges: true },
    (snapshot) => {
      state.error = null;

      const hasPendingWrites = (snapshot as unknown as { metadata?: { hasPendingWrites?: boolean } }).metadata
        ?.hasPendingWrites === true;

      if (snapshot.exists()) {
        const data = snapshot.data() as T;

        if (state.data !== null && writeQueue.isBlocked() && !hasPendingWrites) {
          writeQueue.setDeferred({ exists: true, data });
          state.loading = false;
          return;
        }

        // modifiedAt-based conflict resolution
        const localTime = ((state.data as Record<string, unknown> | null)?.modifiedAt as Timestamp | undefined)?.toMillis?.() ?? 0;
        const incomingTime = ((data as Record<string, unknown>).modifiedAt as Timestamp | undefined)?.toMillis?.() ?? 0;
        if (incomingTime > 0 && localTime > 0 && incomingTime < localTime) {
          state.loading = false;
          return;
        }

        if (state.data === null) {
          state.data = { ...data, docId } as T;
        } else {
          mergeDocumentIntoProxy(
            state.data as Record<string, unknown>,
            data as Record<string, unknown>,
            docId
          );
        }
      } else {
        if (state.data === null && snapshot.metadata.fromCache) {
          return;
        }

        if (state.data !== null && writeQueue.isBlocked() && !hasPendingWrites) {
          writeQueue.setDeferred({ exists: false });
          state.loading = false;
          return;
        }

        state.data = null;
      }
      state.loading = false;
    },
    (error) => {
      state.loading = false;
      state.error = error;
      onError?.(error);
    }
  );

  const cancelUpdateDebounceAndPending = (): void => {
    writeState.updateDebounced?.cancel();
    writeState.updateDebounced = undefined;
    writeState.updatePending = undefined;
  };

  const cancelPending = (): void => {
    cancelNonUpdateDebouncedOps();
    cancelUpdateDebounceAndPending();
    writeState.debouncedError = undefined;
    writeQueue.clearDeferred();
  };

  const updateImmediate = async (data: UpdateData<T>): Promise<void> => {
    await updateDoc(docRef, { ...data, modifiedAt: serverTimestamp() });
  };

  /**
   * Schedules a drain of `updatePending`, creating the debounce if the last run
   * tore it down. Every path that leaves data in `updatePending` goes through
   * here: a payload with no timer behind it would keep `writeQueue.isBlocked()`
   * true forever, and remote snapshots would be deferred for the rest of the
   * session.
   */
  const armUpdateDebounce = (): void => {
    if (!writeState.updateDebounced) {
      writeState.updateDebounced = debounce(() => {
        const settled = runPendingUpdate();
        writeState.updateSettled = settled;
        return settled;
      }, SAVE_DEBOUNCE_MS);
    }
    writeState.updateDebounced();
  };

  /**
   * Drains `updatePending` into one `updateDoc`. Never rejects: the debounce
   * that owns it is fired from a timer, so a rejection here would surface as an
   * unhandled one. Failures go to `onError` and to `debouncedError`, which
   * `flushPending` rethrows, and the payload is re-queued and re-armed until it
   * lands — this write path has no retry bound and never drops data.
   *
   * A success clears `debouncedError`: the retry that lands the payload is what
   * supersedes the failure that parked it, and without this the flush after a
   * self-healed write would report "not saved" over data that did land.
   */
  const runPendingUpdate = async (): Promise<void> => {
    if (writeState.updateInFlight) return;

    const merged = writeState.updatePending;
    if (!merged) return;

    writeState.updatePending = undefined;
    writeState.updateInFlight = merged;

    try {
      await writeQueue.enqueue(async () => {
        writeState.writing = true;
        try {
          await updateImmediate(merged);
        } finally {
          writeState.writing = false;
        }
      });
      writeState.updateInFlight = undefined;
      writeState.debouncedError = undefined;
    } catch (error) {
      const normalized = error instanceof Error ? error : new Error(String(error));
      writeState.updateInFlight = undefined;
      writeState.debouncedError = writeState.debouncedError ?? normalized;
      writeState.updatePending = writeState.updatePending
        ? ({ ...(merged as Record<string, unknown>), ...(writeState.updatePending as Record<string, unknown>) } as UpdateData<T>)
        : merged;
      onError?.(normalized);
    } finally {
      if (writeState.updatePending) {
        armUpdateDebounce();
      } else if (!writeState.updateInFlight && !writeState.writing) {
        writeState.updateDebounced?.cancel();
        writeState.updateDebounced = undefined;
        writeQueue.applyDeferredIfAny();
      }
    }
  };

  /**
   * Drives `updatePending` to empty, one pass per recursion, and rejects if it
   * cannot: a resolved `flushUpdate` means the update queue drained.
   *
   * Firing the armed debounce is not enough on its own: `runPendingUpdate` bails
   * out while another write holds the queue, and the `{ debounce: false }` branch
   * and `set`/`setMerge` take that queue without ever touching `updateSettled`.
   * So each pass first waits out whatever is running — the debounced body via
   * `updateSettled`, anything else via the (never-rejecting) `writeChain` — and
   * only then flushes, which is what makes a payload queued behind an in-flight
   * write actually land before this resolves.
   *
   * It terminates: a failed pass returns at once and leaves the report to
   * `debouncedError`, and `MAX_FLUSH_PASSES` bounds the rest.
   */
  const flushUpdate = async (remaining: number): Promise<void> => {
    await writeState.updateSettled;
    await writeState.writeChain;
    await writeState.updateDebounced?.flush();

    if (writeState.debouncedError) return;
    if (!writeState.updatePending && !writeState.updateInFlight) return;
    if (remaining <= 1) {
      throw new Error(`Pending write to ${path} did not drain within ${MAX_FLUSH_PASSES} flush passes`);
    }

    await flushUpdate(remaining - 1);
  };

  /**
   * Forces every armed debounce to fire now, awaits the writes and rethrows the
   * first failure any of them recorded. `⌘S` needs this because a field's own
   * debounce only reaches `persist.update`, which then arms `SAVE_DEBOUNCE_MS`
   * of its own before anything leaves the client; a caller that armed a
   * debounced write learns it did not land only because this rejects.
   *
   * The parked error is read after the work, and cleared only when the last
   * concurrent flush leaves — a flusher must not wipe a failure another one has
   * not read yet. A failure parked while no flush was running is therefore
   * reported by the next one, which is also the flush that retries it.
   */
  const flushPending = async (): Promise<void> => {
    writeState.flushDepth = (writeState.flushDepth ?? 0) + 1;

    try {
      const outcomes = await Promise.allSettled([
        flushUpdate(MAX_FLUSH_PASSES),
        writeState.setDebounced?.flush(),
        writeState.setMergeDebounced?.flush(),
      ]);

      const parked = writeState.debouncedError;
      if (parked) throw parked;

      const rejected = outcomes.find(
        (outcome): outcome is PromiseRejectedResult => outcome.status === 'rejected'
      );
      if (rejected) throw rejected.reason;
    } finally {
      writeState.flushDepth = (writeState.flushDepth ?? 1) - 1;
      if (writeState.flushDepth === 0) {
        writeState.debouncedError = undefined;
      }
    }
  };

  const update = async (data: UpdateData<T>, options?: PersistOperationOptions): Promise<void> => {
    const shouldDebounce = options?.debounce !== false;
    if (shouldDebounce) {
      writeState.updatePending = writeState.updatePending
        ? { ...writeState.updatePending, ...data }
        : { ...data };

      armUpdateDebounce();
    } else {
      cancelNonUpdateDebouncedOps();

      const preExistingPending = writeState.updatePending;
      const merged = preExistingPending
        ? { ...preExistingPending, ...data }
        : { ...data };
      writeState.updatePending = undefined;
      writeState.updateDebounced?.cancel();
      writeState.updateInFlight = merged;

      try {
        await writeQueue.enqueue(async () => {
          writeState.writing = true;
          try {
            await updateImmediate(merged);
          } finally {
            writeState.writing = false;
          }
        });
        writeState.updateInFlight = undefined;
      } catch (error) {
        writeState.updateInFlight = undefined;
        /**
         * Restores only the pending swept in at the start plus whatever another
         * call armed while this write was in flight — never this call's own
         * failed `data`. The caller rejects and rolls its proxy back, so a
         * silent debounced retry would land the write it just reported as
         * failed (SYD-2413).
         */
        writeState.updatePending = writeState.updatePending
          ? (preExistingPending
              ? ({ ...(preExistingPending as Record<string, unknown>), ...(writeState.updatePending as Record<string, unknown>) } as UpdateData<T>)
              : writeState.updatePending)
          : preExistingPending;
        throw error;
      } finally {
        if (writeState.updatePending) {
          /**
           * `armUpdateDebounce`, not `updateDebounced?.()`: this branch cancelled
           * the debounce on the way in and may have torn it down, and a re-queued
           * payload with no timer behind it wedges the document permanently.
           */
          armUpdateDebounce();
        } else if (!writeState.updateInFlight && !writeState.writing) {
          writeQueue.applyDeferredIfAny();
        }
      }
    }
  };

  /**
   * Resolves `createdAt` in one precedence: the loaded document, then a
   * top-level `createdAt` on the payload, then now.
   *
   * The loaded document wins because it is the value the server holds, while a
   * payload carries whatever the caller assembled — a stale or hand-built
   * `createdAt` must never rewrite the creation time of a document already on
   * screen. The payload is consulted only when nothing is loaded, which is the
   * shape of restoring a deleted document: without it the write re-stamps a
   * creation time the document never had. `modifiedAt` still bumps in both
   * cases, because the write is a genuine change.
   */
  const setImmediate = async (data: T): Promise<void> => {
    const loadedCreatedAt = (state.data as Record<string, unknown> | null)?.createdAt as Timestamp | undefined;
    const payloadCreatedAt = (data as Record<string, unknown>).createdAt as Timestamp | undefined;
    const createdAt = loadedCreatedAt ?? payloadCreatedAt ?? Timestamp.now();
    await setDoc(docRef, { ...data, createdAt, modifiedAt: serverTimestamp() });
  };

  const set = async (data: T, options?: PersistOperationOptions): Promise<void> => {
    const shouldDebounce = options?.debounce !== false;
    if (shouldDebounce) {
      if (!writeState.setDebounced) {
        writeState.setDebounced = debounce(async (d: T) => {
          try {
            await writeQueue.enqueue(async () => {
              writeState.writing = true;
              try {
                await setImmediate(d);
              } finally {
                writeState.writing = false;
              }
            });
          } catch (error) {
            const normalized = error instanceof Error ? error : new Error(String(error));
            writeState.debouncedError = writeState.debouncedError ?? normalized;
            onError?.(normalized);
          } finally {
            writeQueue.applyDeferredIfAny();
          }
        }, SAVE_DEBOUNCE_MS);
      }
      writeState.setDebounced(data);
    } else {
      cancelNonUpdateDebouncedOps();
      cancelUpdateDebounceAndPending();
      await writeQueue.enqueue(async () => {
        writeState.writing = true;
        try {
          await setImmediate(data);
        } finally {
          writeState.writing = false;
        }
      });
      writeQueue.applyDeferredIfAny();
    }
  };

  const setMergeImmediate = async (data: Partial<T>): Promise<void> => {
    await setDoc(docRef, { ...data, modifiedAt: serverTimestamp() }, { merge: true });
  };

  const setMerge = async (data: Partial<T>, options?: PersistOperationOptions): Promise<void> => {
    const shouldDebounce = options?.debounce !== false;
    if (shouldDebounce) {
      if (!writeState.setMergeDebounced) {
        writeState.setMergeDebounced = debounce(async (d: Partial<T>) => {
          try {
            await writeQueue.enqueue(async () => {
              writeState.writing = true;
              try {
                await setMergeImmediate(d);
              } finally {
                writeState.writing = false;
              }
            });
          } catch (error) {
            const normalized = error instanceof Error ? error : new Error(String(error));
            writeState.debouncedError = writeState.debouncedError ?? normalized;
            onError?.(normalized);
          } finally {
            writeQueue.applyDeferredIfAny();
          }
        }, SAVE_DEBOUNCE_MS);
      }
      writeState.setMergeDebounced(data);
    } else {
      cancelNonUpdateDebouncedOps();
      cancelUpdateDebounceAndPending();
      await writeQueue.enqueue(async () => {
        writeState.writing = true;
        try {
          await setMergeImmediate(data);
        } finally {
          writeState.writing = false;
        }
      });
      writeQueue.applyDeferredIfAny();
    }
  };

  const deleteImmediate = async (): Promise<void> => {
    await deleteDoc(docRef);
  };

  const delete_ = async (): Promise<void> => {
    cancelPending();
    await writeQueue.enqueue(async () => {
      writeState.writing = true;
      try {
        await deleteImmediate();
      } finally {
        writeState.writing = false;
      }
    });
    writeQueue.clearDeferred();
    writeQueue.applyDeferredIfAny();
  };

  const persist: DocumentPersist<T> = {
    persistType: 'document',
    docRef,
    update,
    set,
    setMerge,
    delete: delete_,
    cancelPending,
    flushPending,
  };

  return { persist, unsubscribe };
}
